/**
 * Stock Price Service
 * Fetches real-time stock quotes via Supabase Edge Function (secure)
 */

import { getSupabaseClient } from '@/lib/supabase'

export interface StockQuote {
  symbol: string
  currentPrice: number
  change: number
  changePercent: number
  high: number
  low: number
  open: number
  previousClose: number
  timestamp: number
}

/**
 * Fetch stock quote via Supabase Edge Function
 */
export async function getStockQuote(
  symbol: string
): Promise<StockQuote | null> {
  try {
    const supabase = getSupabaseClient()
    if (!supabase) {
      console.warn('Supabase client not initialized')
      return null
    }

    const { data, error } = await supabase.functions.invoke('stock-quote', {
      body: { symbol: symbol.toUpperCase() },
    })

    if (error) {
      console.error(`Failed to fetch quote for ${symbol}:`, error)
      return null
    }

    if (data.error) {
      console.error(`Quote error for ${symbol}:`, data.error)
      return null
    }

    return data as StockQuote
  } catch (error) {
    console.error(`Failed to fetch quote for ${symbol}:`, error)
    return null
  }
}

/**
 * Fetch multiple stock quotes
 * Note: Finnhub free tier has rate limits (60 calls/min)
 */
export async function getMultipleQuotes(
  symbols: string[]
): Promise<Map<string, StockQuote>> {
  const quotes = new Map<string, StockQuote>()

  // Dedupe and filter empty
  const uniqueSymbols = [...new Set(symbols.filter(Boolean))]

  // Fetch sequentially to respect rate limits
  // In production, consider batching with delays
  for (const symbol of uniqueSymbols) {
    const quote = await getStockQuote(symbol)
    if (quote) {
      quotes.set(symbol.toUpperCase(), quote)
    }
    // Small delay to avoid rate limiting
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  return quotes
}

/**
 * Calculate gain/loss from a base price
 */
export function calculateGainLoss(
  currentPrice: number,
  basePrice: number
): { value: number; percent: number } {
  const value = currentPrice - basePrice
  const percent = ((currentPrice - basePrice) / basePrice) * 100
  return { value, percent }
}
